/**
 * Carries a loader's painted progress across the mounts of one flow, so the
 * workspace handoff (sign-in redirect -> callback -> dashboard) reads as one
 * loader instead of restarting at 0 on every page or remount.
 *
 * Values live in memory for client-side route changes. The one full-document
 * hop (the sign-in page assigning the callback URL) opts in to carrying its
 * value through sessionStorage; nothing else writes there, so reloading a page
 * mid-load starts that load over. A stored value is consumed by the next
 * document and expires quickly, so it can never seed an unrelated later load.
 */

export const WORKSPACE_LOADER_CONTINUITY_KEY = "workspace-auth";

const STORAGE_PREFIX = "smartmanager:loader-continuity:";
export const LOADER_CONTINUITY_STORAGE_TTL_MS = 30_000;

type StoredContinuity = { progress: number; savedAt: number };

const painted = new Map<string, number>();
const consumedStorageKeys = new Set<string>();
const carriedKeys = new Set<string>();

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function isResumableProgress(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 && value < 100;
}

function persistCarried(): void {
  const store = storage();
  if (!store) return;
  const savedAt = Date.now();
  for (const key of carriedKeys) {
    const progress = painted.get(key);
    if (progress === undefined) continue;
    try {
      store.setItem(STORAGE_PREFIX + key, JSON.stringify({ progress, savedAt } satisfies StoredContinuity));
    } catch {
      // Storage is optional; the next document simply starts at 0.
    }
  }
}

function consumeStored(key: string, now: number): number | null {
  if (consumedStorageKeys.has(key)) return null;
  consumedStorageKeys.add(key);
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(STORAGE_PREFIX + key);
    store.removeItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredContinuity>;
    if (!isResumableProgress(parsed.progress) || typeof parsed.savedAt !== "number") return null;
    const age = now - parsed.savedAt;
    return age >= 0 && age <= LOADER_CONTINUITY_STORAGE_TTL_MS ? parsed.progress : null;
  } catch {
    return null;
  }
}

/** The progress a new loader for `key` should start from, or null for 0. */
export function readLoaderContinuity(key: string, now = Date.now()): number | null {
  const inMemory = painted.get(key);
  if (inMemory !== undefined) return inMemory;
  const stored = consumeStored(key, now);
  if (stored !== null) painted.set(key, stored);
  return stored;
}

/**
 * Records the latest painted value. A finished loader clears its key, so a
 * completed flow never seeds the next one.
 */
export function recordLoaderContinuity(key: string, progress: number): void {
  if (!isResumableProgress(progress)) {
    if (progress >= 100) clearLoaderContinuity(key);
    return;
  }
  painted.set(key, progress);
}

/**
 * Hands `key` to the next document: whatever the loader has painted when this
 * page hides is where the next page's loader resumes. Call it when starting a
 * navigation that the same flow continues on the other side.
 */
export function carryLoaderContinuityAcrossNavigation(key: string): void {
  if (typeof window === "undefined") return;
  if (carriedKeys.size === 0) window.addEventListener("pagehide", persistCarried);
  carriedKeys.add(key);
}

/** Ends a flow: the next loader for `key` starts at 0. */
export function clearLoaderContinuity(key: string): void {
  painted.delete(key);
  consumedStorageKeys.add(key);
  carriedKeys.delete(key);
  if (carriedKeys.size === 0 && typeof window !== "undefined") {
    window.removeEventListener("pagehide", persistCarried);
  }
  try {
    storage()?.removeItem(STORAGE_PREFIX + key);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** Simulates a new document: drops memory and the consume-once bookkeeping. */
export function resetLoaderContinuityForTests(): void {
  painted.clear();
  consumedStorageKeys.clear();
  carriedKeys.clear();
  if (typeof window !== "undefined") window.removeEventListener("pagehide", persistCarried);
}
